import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Battery, Sparkles, CheckCircle, Heart, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function ActiveRecoveryGuide() {
  const navigate = useNavigate();

  const recoveryOptions = [
    { activity: "Gentle Walk", duration: "20 minutes", intensity: "Very easy pace", benefit: "Promotes blood flow without strain" },
    { activity: "Easy Stretching", duration: "15 minutes", intensity: "No pushing, just relaxing", benefit: "Releases muscle tension" },
    { activity: "Restorative Yoga", duration: "20-30 minutes", intensity: "Mostly floor poses, supported", benefit: "Deep relaxation and recovery" },
    { activity: "Foam Rolling", duration: "10-15 minutes", intensity: "Gentle pressure", benefit: "Releases muscle knots and fascia" },
    { activity: "Swimming (Easy)", duration: "20 minutes", intensity: "Leisurely laps", benefit: "Zero-impact movement" },
    { activity: "Light Tai Chi", duration: "15-20 minutes", intensity: "Slow, flowing movements", benefit: "Improves balance and calm" }
  ];

  const bathRecipe = {
    ingredients: [
      "2 cups Epsom salt (magnesium relaxes muscles)",
      "1/2 cup baking soda (softens water)",
      "10 drops lavender essential oil (optional)",
      "Warm water (not too hot - 92-100°F)"
    ],
    instructions: [
      "Run warm bath water",
      "Add Epsom salt and baking soda while water runs",
      "Add essential oil once bath is filled",
      "Soak for 20-30 minutes",
      "Hydrate with water while soaking"
    ]
  };

  const whyRecoveryMatters = [
    { point: "Muscle Repair", desc: "Your muscles rebuild stronger during rest, not during exercise" },
    { point: "Injury Prevention", desc: "Overtraining leads to strains, stress fractures, and burnout" },
    { point: "Performance Gains", desc: "Rest is when your fitness actually improves" },
    { point: "Mental Refresh", desc: "Recovery days prevent exercise from becoming a chore" },
    { point: "Hormone Balance", desc: "Rest allows cortisol levels to normalize" },
    { point: "Consistency", desc: "Taking breaks helps you maintain long-term habits" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
            Week 4 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Active Recovery
          </h1>
          <p className="text-xl text-secondary-600">Rest is Part of the Work</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-violet-500 to-purple-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Battery className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today: Gentle Movement + Recovery</h3>
              <p className="text-violet-100 text-lg">
                After a week of challenging your body, today is about honoring it with rest. 
                Active recovery means gentle movement that promotes healing without adding stress.
              </p>
            </div>
          </div>
        </section>

        {/* Why Recovery Matters */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Why Recovery Matters</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {whyRecoveryMatters.map((item, index) => (
              <div key={index} className="p-4 bg-violet-50 rounded-xl">
                <h3 className="font-bold text-violet-700 mb-1 flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  {item.point}
                </h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Recovery Options */}
        <section className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 border-2 border-violet-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Choose Your Recovery Activity</h2>
          <div className="space-y-3">
            {recoveryOptions.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <h3 className="font-bold text-violet-700 md:w-1/4">{item.activity}</h3>
                  <span className="text-sm bg-violet-100 text-violet-600 px-2 py-1 rounded-full w-fit">{item.duration}</span>
                  <span className="text-sm text-secondary-500">{item.intensity}</span>
                </div>
                <p className="text-sm text-secondary-600 mt-2">{item.benefit}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Epsom Salt Bath */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Moon className="w-6 h-6 text-violet-500" />
            Tonight's Epsom Salt Bath Recipe
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold text-violet-700 mb-3">Ingredients:</h3>
              <ul className="space-y-2">
                {bathRecipe.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-center gap-2 text-secondary-700">
                    <Sparkles className="w-4 h-4 text-violet-400 flex-shrink-0" />
                    {ing}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-violet-700 mb-3">Instructions:</h3>
              <ol className="space-y-2">
                {bathRecipe.instructions.map((step, i) => (
                  <li key={i} className="flex items-center gap-2 text-secondary-700">
                    <span className="w-5 h-5 bg-violet-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="mt-6 bg-violet-50 rounded-xl p-4">
            <p className="text-secondary-700">
              <strong>Tip:</strong> Add calming music, dim the lights, and treat this as a sacred self-care ritual. 
              You've earned this after a week of movement!
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default ActiveRecoveryGuide;
